import React, { useEffect, useMemo, useRef, useState, ChangeEvent, KeyboardEvent } from "react";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  Row,
} from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { showError, showSuccess } from "toast/toast";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import { getAuthCookie } from "../../utils/authCookies";

type ContactSubmissionRow = {
  _id: string;
  name: string;
  email: string;
  phone_country_code?: string;
  phone?: string;
  message_type?: string;
  message?: string;
  status?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
};

function fuzzyTextFilter<TData>(row: Row<TData>, columnId: string, filterValue: string) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "string" ? raw : String(raw || "")).toLowerCase();
  return value.includes((filterValue || "").toLowerCase());
}

const columnHelper = createColumnHelper<ContactSubmissionRow>();

const statusPill = (s?: string) => {
  const v = (s || "").toLowerCase();
  const cls =
    v === "reviewed"
      ? "bg-success-subtle text-success-emphasis"
      : v === "pending"
        ? "bg-warning-subtle text-warning-emphasis"
        : "bg-primary-subtle text-primary-emphasis";
  return <span className={`badge rounded-pill px-3 py-2 ${cls}`}>{s || "--"}</span>;
};

const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "short" }) : "--");

const SuperAdminContactRequests: React.FC = () => {
  const token = typeof window !== "undefined" ? getAuthCookie("sessionToken") : null;
  const [rows, setRows] = useState<ContactSubmissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [deleting, setDeleting] = useState<string | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetName, setDeleteTargetName] = useState("");
  const topRef = useRef<HTMLDivElement>(null);

  const handleDeleteClick = (row: ContactSubmissionRow) => {
    setDeleteTargetId(row._id);
    setDeleteTargetName(row.name || row.email || "Contact submission");
    setOpenDelete(true);
  };

  const confirmDeleteContact = async () => {
    if (!deleteTargetId || !token) return;
    setDeleting(deleteTargetId);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/contact-submissions/${deleteTargetId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      let json: any = {};
      try {
        const text = await res.text();
        if (text) json = JSON.parse(text);
      } catch {
        // empty or non-JSON body is ok when res.ok
      }
      if (!res.ok || json?.error) {
        showError(json?.error || json?.message || "Failed to delete");
        return;
      }
      showSuccess("Contact submission deleted successfully!");
      setRows((prev) => prev.filter((r) => r._id !== deleteTargetId));
      setRowCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setDeleting(null);
      setOpenDelete(false);
      setDeleteTargetId(null);
      setDeleteTargetName("");
    }
  };

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      try {
        const body = {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search: globalFilter,
          filters: statusFilter ? { status: statusFilter } : {},
          sort: { field: sortField, order: sortOrder },
        };

        const res = await fetch(`${process.env.REACT_APP_API_URL}/contact-submissions/datatable`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
        });

        const json: any = await res.json();
        if (!res.ok || json?.error) {
          const msg = json?.error || json?.message || "Failed to fetch contact submissions";
          showError(msg);
          throw new Error(msg);
        }

        // Response shape: { data: [], limit, page, total, totalPages }
        const dataArray = Array.isArray(json?.data) ? json.data : [];
        const total = Number(json?.total) ?? 0;

        if (!ignore) {
          setRows(dataArray);
          setRowCount(total);
        }
      } catch (e) {
        if (!ignore) {
          setRows([]);
          setRowCount(0);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => { ignore = true; };
  }, [token, pagination.pageIndex, pagination.pageSize, globalFilter, statusFilter, sortField, sortOrder]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("_id", {
        id: "_id",
        header: () => null,
        cell: () => null,
        enableHiding: true,
      }),
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: () => <div className="th-text">Name</div>,
        cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor("email", {
        id: "email",
        header: () => <div className="th-text">Email</div>,
        cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.display({
        id: "phone",
        header: () => <div className="th-text">Phone</div>,
        cell: (info) => {
          const r = info.row.original;
          const phone = [r.phone_country_code, r.phone].filter(Boolean).join(" ") || "--";
          return <div className="text-sm">{phone}</div>;
        },
      }),
      columnHelper.accessor("message_type", {
        id: "message_type",
        header: () => <div className="th-text">Message Type</div>,
        cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      // columnHelper.accessor("message", {
      //   id: "message",
      //   header: () => <div className="th-text">Message</div>,
      //   cell: (info) => (
      //     <div className="text-sm text-truncate" style={{ maxWidth: 260 }}>
      //       {info.getValue() || "--"}
      //     </div>
      //   ),
      //   filterFn: fuzzyTextFilter,
      //   enableGlobalFilter: true,
      // }),
      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="th-text">Status</div>,
        cell: (info) => <div className="text-sm">{statusPill(info.getValue())}</div>,
      }),
      columnHelper.accessor("created_at", {
        id: "created_at",
        header: () => <div className="th-text">Requested on</div>,
        cell: (info) => <div className="text-sm">{fmtDate(info.getValue())}</div>,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text ">Actions</div>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              <ViewIconButton
                to={`/superadmin/contact-requests/${row._id}`}
                className="btn action-btn "
                title="View"
              />
              <EditIconButton
                to={`/superadmin/contact-requests/${row._id}/edit`}
                className="icon-square"
                title="Edit"
              />
              <DeleteIconButton
                title="Delete"
                className="icon-square danger"
                onClick={() => handleDeleteClick(row)}
                disabled={deleting === row._id}
              />
            </div>
          );
        },
      }),
    ],
    [pagination.pageIndex, pagination.pageSize, deleting]
  );

  const table = useReactTable({
    data: rows,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize) || 1,
    state: { pagination, globalFilter, columnVisibility: { _id: false } },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [pagination.pageIndex]);

  return (
    <div className="content" ref={topRef}>
      <div className="po-header mb-4 shadow-border-datatables">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Contact Requests</h4>
          </div>
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            <select
              className="form-select form-select-sm"
              style={{ width: "auto" }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
            </select>
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, email, message..."
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") setGlobalFilter(searchInput);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />

        <div className="rdt-desktop shadow-sm">
          <div className="production-scroll" style={{ "--rdt-min": "1200px" } as React.CSSProperties}>
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="_id"
            titleColId="name"
            actionColId="status"
          />
        </div>
      </div>

      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          {loading ? "Loading..." : "No records found"}
        </div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      )}

      <DeleteConfirmModal
        open={openDelete}
        dynamicName={deleteTargetName || "Contact submission"}
        onCancel={() => {
          setOpenDelete(false);
          setDeleteTargetId(null);
          setDeleteTargetName("");
        }}
        onDelete={confirmDeleteContact}
      />
    </div>
  );
};

export default SuperAdminContactRequests;
